import { useState, useEffect } from 'react';
import { accountAPI, keywordAPI, automationAPI, instagramOAuthAPI, contentSettingsAPI, customPromptAPI } from '../../../services/api';

export function useAutomationSetup(user) {
  // ===== 1단계: 계정 설정 =====
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // ===== 2단계: 콘텐츠 설정 =====
  const [keyword, setKeyword] = useState('');
  const [hashtags, setHashtags] = useState(['자동화', '코딩']);
  const [hashtagInput, setHashtagInput] = useState('');
  const [contentSettings, setContentSettings] = useState({
    businessName: '',
    promotionGoal: '',
    postStyle: '',
    accountCategory: '',
    accountSubCategory: '',
    targetAgeGroup: '',
    targetGender: '',
    targetInterest: '',
    toneAndManner: '',
    brandColor: '#1313EC',
    imageMood: '',
    defaultCaption: '',
    permanentHashtags: '',
    keyProducts: '',
    coreValue: '',
    contentRatio: '',
    imageGenerationEnabled: true,
  });
  const [contentPillars, setContentPillars] = useState([]);
  const [aiSuggestionLoading, setAiSuggestionLoading] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [isCustomPrompt, setIsCustomPrompt] = useState(false);
  const [showAdvancedPrompt, setShowAdvancedPrompt] = useState(false);

  // ===== 4단계: 스케줄러 =====
  const [selectedDays, setSelectedDays] = useState([true, true, true, true, true, false, false]);
  const [imageGenDays, setImageGenDays] = useState([true, true, true, true, true, true, true]);
  const [uploadTime, setUploadTime] = useState('09:00');
  const [postsPerDay, setPostsPerDay] = useState('1');

  // ===== 공통 상태 =====
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showOAuthGuide, setShowOAuthGuide] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const isUserRole = user?.role === 'USER';

  useEffect(() => {
    loadInitialData();
  }, []);

  // 페이지 이탈 경고 (수정 후 저장 안 한 경우)
  useEffect(() => {
    // SPA 내 탭 전환 감지용 전역 플래그
    window.__automationDirty = isDirty;
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.__automationDirty = false;
    };
  }, [isDirty]);

  useEffect(() => {
    if (currentStep > 1 && !isEditMode) {
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 100);
    }
  }, [currentStep, isEditMode]);

  const loadInitialData = async () => {
    const loadedAccount = await fetchAccounts();
    if (loadedAccount?.id) {
      await restoreAutomationSettings(loadedAccount.id);
    }
  };

  const restoreAutomationSettings = async (accountId) => {
    try {
      const response = await automationAPI.getStatus(accountId);
      if (!response.success || !response.data) return;

      const { keyword: savedKeyword, schedule } = response.data;

      if (savedKeyword?.keyword) {
        setKeyword(savedKeyword.keyword);
      }

      if (schedule) {
        if (schedule.uploadTimes?.length > 0) {
          setUploadTime(schedule.uploadTimes[0]);
        }
        if (schedule.daysOfWeek?.length > 0) {
          const days = [false, false, false, false, false, false, false];
          schedule.daysOfWeek.forEach(d => {
            if (d >= 1 && d <= 7) days[d - 1] = true;
          });
          setSelectedDays(days);
        }
      }

      if (savedKeyword?.id) {
        try {
          const kwResponse = await keywordAPI.getByAccountId(accountId);
          if (kwResponse.success && kwResponse.data?.length > 0) {
            const activeKw = kwResponse.data.find(k => k.isActive) || kwResponse.data[0];
            if (activeKw?.keyword) {
              setKeyword(activeKw.keyword);
            }
          }
        } catch {
          // 키워드 로드 실패 무시
        }
      }

      await loadContentSettings(accountId);
      await loadCustomPrompt(accountId);

      if (savedKeyword?.keyword && schedule?.uploadTimes?.length > 0) {
        setIsEditMode(true);
        setCurrentStep(3);
      }
    } catch (err) {
      console.debug('자동화 설정 미존재:', err.message);
    }
  };

  // ========== 1단계: 계정 관리 ==========
  const fetchAccounts = async () => {
    try {
      if (isUserRole) {
        const response = await accountAPI.getMy();
        if (response.success && response.data?.length > 0) {
          setAccounts(response.data);
          const activeAccount = response.data.find(acc => acc.status === 'ACTIVE' && acc.platformUserId) || response.data[0];
          setSelectedAccount(activeAccount);
          if (activeAccount.status === 'ACTIVE' && activeAccount.platformUserId) {
            setCurrentStep(2);
          }
          return activeAccount;
        }
      } else {
        const response = await accountAPI.getAll();
        if (response.success) {
          setAccounts(response.data || []);
          const activeAccount = (response.data || []).find(acc => acc.status === 'ACTIVE');
          if (activeAccount) {
            setSelectedAccount(activeAccount);
            return activeAccount;
          }
        }
      }
    } catch (err) {
      console.error('계정 로드 실패:', err);
    }
    return null;
  };

  // ========== Instagram OAuth ==========
  const handleInstagramConnect = () => {
    setShowOAuthGuide(true);
  };

  const startOAuthFlow = () => {
    setShowOAuthGuide(false);
    const authorizeUrl = instagramOAuthAPI.getAuthorizeUrl();
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    const popup = window.open(
      authorizeUrl,
      'instagram-oauth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    );

    const handleMessage = (event) => {
      if (event.data?.type === 'INSTAGRAM_OAUTH_SUCCESS') {
        setSuccess('인스타그램 계정이 연결되었습니다!');
        fetchAccounts().then(() => setCurrentStep(2));
        window.removeEventListener('message', handleMessage);
      } else if (event.data?.type === 'INSTAGRAM_OAUTH_ERROR') {
        setError(`인스타그램 연결 실패: ${event.data.error}`);
        window.removeEventListener('message', handleMessage);
      }
    };

    window.addEventListener('message', handleMessage);

    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        window.removeEventListener('message', handleMessage);
      }
    }, 1000);
  };

  const handleDisconnectAccount = async (accountId) => {
    if (!window.confirm('인스타그램 연결을 해제하시겠습니까?')) return;
    try {
      setLoading(true);
      await instagramOAuthAPI.disconnect(accountId);
      setSuccess('인스타그램 연결이 해제되었습니다.');
      await fetchAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ========== 2단계: 콘텐츠 설정 ==========
  const loadContentSettings = async (accountId) => {
    try {
      const response = await contentSettingsAPI.get(accountId);
      if (response.success && response.data) {
        const data = response.data;
        setContentSettings({
          businessName: data.businessName || '',
          promotionGoal: data.promotionGoal || '',
          postStyle: data.postStyle || '',
          accountCategory: data.accountCategory || '',
          accountSubCategory: data.accountSubCategory || '',
          targetAgeGroup: data.targetAgeGroup || '',
          targetGender: data.targetGender || '',
          targetInterest: data.targetInterest || '',
          toneAndManner: data.toneAndManner || '',
          brandColor: data.brandColor || '#1313EC',
          imageMood: data.imageMood || '',
          defaultCaption: data.defaultCaption || '',
          permanentHashtags: data.permanentHashtags || '',
          keyProducts: data.keyProducts || '',
          coreValue: data.coreValue || '',
          contentRatio: data.contentRatio || '',
          imageGenerationEnabled: data.imageGenerationEnabled !== false,
        });
        if (data.imageGenerationDays) {
          const days = data.imageGenerationDays.split(',').map(d => parseInt(d.trim()));
          const genDays = [false, false, false, false, false, false, false];
          days.forEach(d => { if (d >= 1 && d <= 7) genDays[d - 1] = true; });
          setImageGenDays(genDays);
        }
        if (data.contentPillars && data.contentPillars.length > 0) {
          setContentPillars(data.contentPillars);
        }
        if (data.permanentHashtags) {
          const tags = data.permanentHashtags.split(',')
            .map(t => t.replace(/^#/, '').trim())
            .filter(t => t);
          if (tags.length > 0) setHashtags(tags);
        }
      }
    } catch (err) {
      console.error('콘텐츠 설정 로드 실패:', err);
    }
  };

  const handleAiProfileSuggestion = async () => {
    if (!contentSettings.businessName && !contentSettings.accountCategory) {
      setError('상호명 또는 업종 카테고리를 먼저 입력해주세요');
      return;
    }
    setAiSuggestionLoading(true);
    try {
      const response = await contentSettingsAPI.generateProfileSuggestion({
        businessName: contentSettings.businessName,
        accountCategory: contentSettings.accountCategory,
        accountSubCategory: contentSettings.accountSubCategory,
      });
      if (response.success && response.data) {
        const s = response.data;
        setContentSettings(prev => ({
          ...prev,
          promotionGoal: s.promotionGoal || prev.promotionGoal,
          postStyle: s.postStyleSuggestion || prev.postStyle,
          targetInterest: s.targetInterestSuggestion || prev.targetInterest,
          toneAndManner: s.toneSuggestion || prev.toneAndManner,
        }));
        setSuccess('AI 추천이 적용되었습니다. 필요하면 수정해주세요!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('AI 추천 생성 실패:', err);
      setError('AI 추천 생성에 실패했습니다. 직접 입력해주세요.');
    } finally {
      setAiSuggestionLoading(false);
    }
  };

  const saveContentSettings = async () => {
    if (!selectedAccount) return;
    try {
      const imageGenDaysStr = imageGenDays
        .map((enabled, index) => enabled ? index + 1 : null)
        .filter(d => d !== null)
        .join(',');
      const payload = {
        ...contentSettings,
        permanentHashtags: hashtags.map(t => `#${t}`).join(','),
        imageGenerationDays: imageGenDaysStr || null,
        contentPillars: contentPillars.map((p, i) => ({
          title: p.title,
          description: p.description || '',
          frequency: p.frequency || '',
          sortOrder: i,
        })),
      };
      await contentSettingsAPI.update(selectedAccount.id, payload);
    } catch (err) {
      console.error('콘텐츠 설정 저장 실패:', err);
    }
  };

  const loadCustomPrompt = async (accountId) => {
    try {
      const response = await customPromptAPI.get(accountId);
      if (response.success && response.data) {
        setCustomPrompt(response.data.prompt || '');
        setIsCustomPrompt(response.data.isCustom || false);
      }
    } catch (err) {
      console.error('커스텀 프롬프트 로드 실패:', err);
    }
  };

  const saveCustomPrompt = async () => {
    if (!selectedAccount) return;
    try {
      await customPromptAPI.update(selectedAccount.id, { customPromptTemplate: customPrompt });
      setIsCustomPrompt(true);
      setSuccess('커스텀 프롬프트가 저장되었습니다.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('커스텀 프롬프트 저장 실패: ' + err.message);
    }
  };

  const resetCustomPrompt = async () => {
    if (!selectedAccount) return;
    try {
      await customPromptAPI.reset(selectedAccount.id);
      setIsCustomPrompt(false);
      await loadCustomPrompt(selectedAccount.id);
      setSuccess('기본 프롬프트로 복원되었습니다.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('프롬프트 복원 실패: ' + err.message);
    }
  };

  const addContentPillar = () => {
    setContentPillars([...contentPillars, {
      title: '',
      description: '',
      frequency: '매주 월요일',
      sortOrder: contentPillars.length,
    }]);
  };

  const removeContentPillar = (index) => {
    setContentPillars(contentPillars.filter((_, i) => i !== index));
  };

  const updateContentPillar = (index, field, value) => {
    const updated = [...contentPillars];
    updated[index] = { ...updated[index], [field]: value };
    setContentPillars(updated);
  };

  const addHashtag = () => {
    if (hashtagInput.trim() && !hashtags.includes(hashtagInput.trim())) {
      setHashtags([...hashtags, hashtagInput.trim()]);
      setHashtagInput('');
    }
  };

  const removeHashtag = (tag) => {
    setHashtags(hashtags.filter(t => t !== tag));
  };

  const toggleDay = (index) => {
    const newDays = [...selectedDays];
    newDays[index] = !newDays[index];
    setSelectedDays(newDays);
  };

  // ========== 자동화 시작 ==========
  const handleStartAutomation = async () => {
    if (!selectedAccount) {
      setError('계정을 선택해주세요');
      return;
    }

    const autoKeyword = contentSettings.businessName?.trim() || keyword.trim();
    if (!autoKeyword) {
      setError('상호명/브랜드명을 입력해주세요');
      return;
    }

    if (hashtags.length === 0) {
      setError('최소 1개의 해시태그를 입력해주세요');
      return;
    }

    if (selectedDays.filter(Boolean).length === 0) {
      setError('최소 1개의 요일을 선택해주세요');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await saveContentSettings();

      const response = await automationAPI.setup({
        accountId: selectedAccount.id,
        keyword: autoKeyword,
        hashtags: hashtags,
        uploadTimes: [uploadTime],
        daysOfWeek: selectedDays
          .map((selected, index) => (selected ? index + 1 : null))
          .filter(day => day !== null),
        startImmediately: false
      });

      if (response.success) {
        setSuccess('자동화 설정이 완료되었습니다!');
        setIsDirty(false);
        window.__automationDirty = false;

        alert(
          `자동화가 시작되었습니다!\n\n` +
          `계정: ${response.data.account.username}\n` +
          `업로드 시간: ${response.data.schedule.uploadTimes.join(', ')}\n` +
          `다음 실행: ${response.data.nextScheduledTime ?
            new Date(response.data.nextScheduledTime).toLocaleString('ko-KR') :
            '설정된 시간에 자동 실행'}`
        );

        setCurrentStep(3);
        setIsEditMode(true);
      }
    } catch (err) {
      setError('자동화 시작 실패: ' + err.message);
      console.error('자동화 설정 오류:', err);
    } finally {
      setLoading(false);
    }
  };

  // dirty 래핑 헬퍼
  const dirtySet = (setter) => (val) => { setIsDirty(true); setter(val); };

  return {
    // 계정
    accounts, selectedAccount, setSelectedAccount,
    // 콘텐츠 설정
    keyword, setKeyword: dirtySet(setKeyword),
    hashtags, hashtagInput, setHashtagInput,
    contentSettings, setContentSettings: dirtySet(setContentSettings), contentPillars,
    aiSuggestionLoading, customPrompt, setCustomPrompt: dirtySet(setCustomPrompt),
    isCustomPrompt, showAdvancedPrompt, setShowAdvancedPrompt,
    // 스케줄러
    selectedDays, imageGenDays, setImageGenDays: dirtySet(setImageGenDays),
    uploadTime, setUploadTime: dirtySet(setUploadTime),
    postsPerDay, setPostsPerDay: dirtySet(setPostsPerDay),
    // 공통
    loading, error, setError, success, setSuccess,
    currentStep, setCurrentStep, isEditMode, isDirty, showOAuthGuide, setShowOAuthGuide,
    isUserRole,
    // 액션
    fetchAccounts, handleInstagramConnect, startOAuthFlow, handleDisconnectAccount,
    handleAiProfileSuggestion, saveCustomPrompt, resetCustomPrompt,
    addContentPillar: () => { setIsDirty(true); addContentPillar(); },
    removeContentPillar: (i) => { setIsDirty(true); removeContentPillar(i); },
    updateContentPillar: (i, f, v) => { setIsDirty(true); updateContentPillar(i, f, v); },
    addHashtag: () => { setIsDirty(true); addHashtag(); },
    removeHashtag: (t) => { setIsDirty(true); removeHashtag(t); },
    toggleDay: (i) => { setIsDirty(true); toggleDay(i); },
    handleStartAutomation,
  };
}
